import React from 'react';

// Individual Responses Table Component with theme support
export const IndividualResponsesTable = ({ responses, loading, pagination, onPageChange, groupBySession, onToggleGrouping, isDarkMode }) => {
  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-demoraes-green mx-auto mb-3"></div>
        <p className={`${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>Carregando respostas...</p>
      </div>
    );
  }

  if ((!responses || !responses.length) && (!pagination.sessions || !pagination.sessions.length)) {
    return (
      <div className="text-center py-8">
        <div className="text-6xl mb-4">📭</div>
        <h3 className={`text-xl font-semibold mb-2 ${
          isDarkMode ? 'text-white' : 'text-slate-800'
        }`}>Nenhuma resposta foi encontrada</h3>
        <p className={`${
          isDarkMode ? 'text-gray-300' : 'text-slate-600'
        }`}>de acordo com os filtros selecionados. Tente ajustar os filtros ou aguarde novas respostas.</p>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (isDarkMode) {
      if (score <= 2) return 'text-red-400 bg-red-900/30';
      if (score === 3) return 'text-yellow-400 bg-yellow-900/30';
      return 'text-green-400 bg-green-900/30';
    } else {
      if (score <= 2) return 'text-red-600 bg-red-50';
      if (score === 3) return 'text-yellow-600 bg-yellow-50';
      return 'text-green-600 bg-green-50';
    }
  };

  return (
    <div>
      {/* Toggle View */}
      <div className="flex justify-between items-center mb-4">
        <h4 className={`text-lg font-semibold ${
          isDarkMode ? 'text-white' : 'text-slate-800'
        }`}>
          {groupBySession ? 'Respostas Agrupadas por Sessão' : 'Respostas Individuais'}
        </h4>
        <button
          onClick={onToggleGrouping}
          className={`px-4 py-2 rounded-lg text-sm transition-colors ${
            isDarkMode 
              ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' 
              : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
          }`}
        >
          {groupBySession ? '📋 Ver Individual' : '📁 Agrupar por Sessão'}
        </button>
      </div>

      {groupBySession && pagination.sessions ? (
        // Grouped by session view
        <div className="space-y-6">
          {pagination.sessions.map(session => (
            <div key={session.session_id} className={`rounded-lg border overflow-hidden ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-600' 
                : 'bg-white border-slate-200'
            }`}>
              <div className={`px-6 py-4 border-b ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h5 className={`font-semibold ${
                      isDarkMode ? 'text-white' : 'text-slate-800'
                    }`}>
                      Sessão: {session.session_id.substring(0, 8)}...
                    </h5>
                    <div className={`flex gap-4 text-sm mt-1 ${
                      isDarkMode ? 'text-gray-300' : 'text-slate-600'
                    }`}>
                      <span>Cliente: {session.tag}</span>
                      <span>Departamento: {session.department}</span>
                      <span>Cargo: {session.position}</span>
                      <span>Tempo: {session.company_time}</span>
                    </div>
                  </div>
                  <div className={`text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-slate-500'
                  }`}>
                    {session.responses.length} respostas
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="grid gap-4">
                  {session.responses.map((response) => (
                    <div key={`${response.dimension}-${response.question_id}`} className={`flex justify-between items-start p-4 rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700' 
                        : 'bg-slate-50'
                    }`}>
                      <div className="flex-1">
                        <div className={`font-medium mb-1 ${
                          isDarkMode ? 'text-white' : 'text-slate-800'
                        }`}>
                          {response.dimension_label}
                        </div>
                        <div className={`text-sm ${
                          isDarkMode ? 'text-gray-300' : 'text-slate-600'
                        }`}>
                          {response.question_text}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="response-score-container">
                          <span 
                            className={`response-score-number px-2 py-1 rounded-full text-xs font-semibold ${getScoreColor(response.score)}`}
                          >
                            {response.score}
                          </span>
                          <div 
                            className={`response-score-label text-xs ${
                              isDarkMode ? 'text-gray-400' : 'text-slate-500'
                            }`}
                          >
                            {response.score_label}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Individual responses table
        <div className="overflow-x-auto">
          <table className={`w-full rounded-lg shadow-sm border ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-600' 
              : 'bg-white border-slate-200'
          }`}>
            <thead className={`${
              isDarkMode ? 'bg-gray-700' : 'bg-slate-50'
            }`}>
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-slate-600'
                }`}>Sessão</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-slate-600'
                }`}>Cliente</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-slate-600'
                }`}>Departamento</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-slate-600'
                }`}>Cargo</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-slate-600'
                }`}>Tempo</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-slate-600'
                }`}>Dimensão</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-slate-600'
                }`}>Pergunta</th>
                <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-300' : 'text-slate-600'
                }`}>Resposta</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${
              isDarkMode ? 'divide-gray-600' : 'divide-slate-200'
            }`}>
              {responses.map((response) => (
                <tr key={`${response.session_id}-${response.question_id}`} className={`transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-slate-50'
                }`}>
                  <td className={`px-4 py-3 text-sm font-mono ${
                    isDarkMode ? 'text-gray-300' : 'text-slate-600'
                  }`}>
                    {response.session_id.substring(0, 8)}...
                  </td>
                  <td className={`px-4 py-3 text-sm font-medium ${
                    isDarkMode ? 'text-white' : 'text-slate-800'
                  }`}>
                    {response.tag || 'N/A'}
                  </td>
                  <td className={`px-4 py-3 text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-slate-600'
                  }`}>
                    {response.department}
                  </td>
                  <td className={`px-4 py-3 text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-slate-600'
                  }`}>
                    {response.position}
                  </td>
                  <td className={`px-4 py-3 text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-slate-600'
                  }`}>
                    {response.company_time}
                  </td>
                  <td className={`px-4 py-3 text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-slate-600'
                  }`}>
                    <span className={`px-2 py-1 rounded text-xs ${
                      isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {response.dimension_label}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-sm max-w-md ${
                    isDarkMode ? 'text-white' : 'text-slate-800'
                  }`}>
                    <div className="truncate" title={response.question_text}>
                      {response.question_text}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-center" style={{minWidth: '140px'}}>
                    <div className="response-score-container">
                      <span 
                        className={`response-score-number px-2 py-1 rounded-full text-xs font-semibold ${getScoreColor(response.score)}`}
                      >
                        {response.score}
                      </span>
                      <span 
                        className={`response-score-label text-xs ${
                          isDarkMode ? 'text-gray-400' : 'text-slate-600'
                        }`}
                      >
                        {response.score_label}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className={`text-sm ${
            isDarkMode ? 'text-gray-300' : 'text-slate-600'
          }`}>
            {groupBySession ? (
              `Mostrando ${pagination.sessions?.length || 0} respondentes de ${pagination.total_sessions || 0}`
            ) : (
              `Mostrando ${((pagination.page - 1) * pagination.page_size) + 1} até ${Math.min(pagination.page * pagination.page_size, pagination.total)} de ${pagination.total} respostas`
            )}
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className={`px-3 py-2 text-sm rounded-lg disabled:opacity-50 transition-colors ${
                isDarkMode 
                  ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' 
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              }`}
            >
              ← Anterior
            </button>
            
            {Array.from({length: Math.min(5, pagination.total_pages)}, (_, i) => {
              let pageNumber;
              if (pagination.total_pages <= 5) {
                pageNumber = i + 1;
              } else if (pagination.page <= 3) {
                pageNumber = i + 1;
              } else if (pagination.page >= pagination.total_pages - 2) {
                pageNumber = pagination.total_pages - 4 + i;
              } else {
                pageNumber = pagination.page - 2 + i;
              }
              
              return (
                <button
                  key={pageNumber}
                  onClick={() => onPageChange(pageNumber)}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                    pageNumber === pagination.page
                      ? 'bg-demoraes-green text-white'
                      : isDarkMode 
                        ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                        : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.total_pages}
              className={`px-3 py-2 text-sm rounded-lg disabled:opacity-50 transition-colors ${
                isDarkMode 
                  ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' 
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              }`}
            >
              Próxima →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
